import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import { setCategoryCityStateDropdowns } from "redux/globalData/globalDataAction";

import AInput from "views/pages/manage/common/formFeilds/customInput/AInput";
import AAutoComplete from "views/pages/manage/common/form-fields-mui/AAutoComplete";
import { LastUpdatedDetails } from "../../stockManagement/materialDistribution/helper";
import CustomDatePicker from "views/pages/manage/common/commonComponents/CustomDatePicker";
import { InputLabelStyle } from "views/pages/manage/masters/studentFinancials/paymentCollection/manage-receipts/constants";
import {
  successToast,
  failureToast,
} from "views/pages/manage/common/utils/methods/toasterFunctions/function";
import { regex } from "views/pages/manage/common/constant";

import TicketHeader from "./TicketHeader";
import {
  BloodGroupOptions,
  GenderOptions,
  isValidMobile,
  updateContactDetails,
  updateStudentDetails,
  validatePincode,
} from "./helper";
import styles from "./styles.module.css";
import moment from "moment";
import ViewEditLogs from "./ViewEditLogs";
import DiscardDialog from "./DiscardDialog";

const _ = require("lodash");

const EditStudentDetails = ({
  exitEditMode,
  ticketDetails,
  changeTicketDetails,
  studentDetailsResponse,
  saveStudentDetails,
  contactDetailsResponse,
  saveContactDetails,
  parentDetailsResponse,
  saveGuardianDetails,
}) => {
  const dispatch = useDispatch();
  const masterDropdowns = useSelector(
    (state) => state.globalData.masterDropdowns
  );

  const [studentDetails, setStudentDetails] = useState(studentDetailsResponse);
  const [contactDetails, setContactDetails] = useState(contactDetailsResponse);
  const [parentInfo, setParentInfo] = useState(parentDetailsResponse);

  const [isStudentDetailsModified, setIsStudentDetailsModified] =
    useState(false);
  const [isContactDetailsModified, setIsContactDetailsModified] =
    useState(false);
  const [isParentInfoModified, setIsParentInfoModified] = useState(false);

  const [viewLogsDialog, setViewLogsDialog] = useState({
    isOpen: false,
    category: "",
    heading: "",
    psid: "",
  });

  const [discardDialog, setDiscardDialog] = useState({
    isOpen: false,
    onConfirm: () => {},
  });

  useEffect(() => {
    dispatch(setCategoryCityStateDropdowns());
  }, []);

  useEffect(() => {
    setStudentDetails(studentDetailsResponse);
  }, [studentDetailsResponse]);

  useEffect(() => {
    setContactDetails(contactDetailsResponse);
  }, [contactDetailsResponse]);

  useEffect(() => {
    setParentInfo(parentDetailsResponse);
  }, [parentDetailsResponse]);

  useEffect(() => {
    if (contactDetails.postalCode.trim().length < 3) return;

    const debounced = _.debounce(() => {
      checkPincode();
    }, 500);

    debounced();

    return () => debounced.cancel();
  }, [contactDetails.postalCode]);

  async function checkPincode() {
    const response = await validatePincode(
      contactDetails["postalCode"],
      masterDropdowns
    );

    if (response) {
      const { cityText, stateText, countryText } = response;

      setContactDetails((prev) => ({
        ...prev,
        city: cityText,
        state: stateText,
        country: countryText,
      }));
    } else {
      setContactDetails((prev) => ({
        ...prev,
        city: "",
        state: "",
        country: "",
      }));
      return failureToast("Invalid pincode!");
    }
  }

  const closeDiscardDialog = () => setDiscardDialog({ isOpen: false });

  const sections = [
    {
      heading: "Student Details",
      state: studentDetails,
      viewLogs: () => {
        setViewLogsDialog({
          isOpen: true,
          category: "studentDetails",
          heading: "Student Details",
          psid: studentDetails.psid,
        });
      },
      saveChanges: async () => {
        if (
          !studentDetails["studentFirstName"]?.trim() ||
          !studentDetails["studentLastName"]?.trim() ||
          !studentDetails["dob"] ||
          !studentDetails["gender"] ||
          !studentDetails["bloodGroup"] ||
          !studentDetails["studentCategory"] ||
          !studentDetails["school"]?.trim()
        ) {
          return failureToast("Please fill all the required fields.");
        } else {
          const payload = {
            studentFirstName: studentDetails["studentFirstName"].trim(),
            studentLastName: studentDetails["studentLastName"].trim(),
            dob: moment(studentDetails["dob"]).format("YYYY-MM-DD"),
            gender: studentDetails["gender"],
            bloodGroup: studentDetails["bloodGroup"],
            studentCategory: studentDetails["studentCategory"],
            school: studentDetails["school"].trim(),

            ticketId: ticketDetails.id,
            ticketDate: ticketDetails.date,
            ticketDescription: ticketDetails.description,
          };

          const response = await updateStudentDetails({
            psid: studentDetails.psid,
            payload,
          });
          if (response?.code === 200) {
            setIsStudentDetailsModified(false);
            saveStudentDetails(response.data);
            return successToast(
              `Student Details successfully updated as part of Ticket Id :${ticketDetails.id}`
            );
          }

          // return failureToast(`Error in updating. Please try again.`);
        }
      },
      isModified: isStudentDetailsModified,
      discardChanges: () => {
        setDiscardDialog({
          isOpen: true,
          onConfirm: () => {
            setStudentDetails(studentDetailsResponse);
            setIsStudentDetailsModified(false);
            closeDiscardDialog();
          },
        });
      },
      onFieldChange: (key, value) => {
        setIsStudentDetailsModified(true);
        setStudentDetails((prev) => ({ ...prev, [key]: value ?? [] }));
      },
      fields: [
        {
          type: "text",
          label: "First Name*",
          key: "studentFirstName",
        },
        {
          type: "text",
          label: "Last Name*",
          key: "studentLastName",
        },
        {
          type: "date",
          label: "Date of Birth*",
          key: "dob",
        },
        {
          type: "dropdown",
          label: "Gender*",
          key: "gender",
          options: GenderOptions,
        },
        {
          type: "dropdown",
          label: "Blood Group*",
          key: "bloodGroup",
          options: BloodGroupOptions,
        },
        {
          type: "dropdown",
          label: "Category*",
          key: "studentCategory",
          options: masterDropdowns.category || [],
        },
        {
          type: "text",
          label: "School studying in*",
          key: "school",
        },
      ],
      lastUpdated: studentDetails.updatedOn
        ? LastUpdatedDetails(
            studentDetails.updatedOn,
            studentDetails?.updatedBy
          )
        : "-",
    },
    {
      heading: "Contact Information",
      state: contactDetails,
      viewLogs: () => {
        setViewLogsDialog({
          isOpen: true,
          category: "studentContactDetails",
          heading: "Contact Information",
          psid: studentDetails.psid,
        });
      },
      saveChanges: async () => {
        if (!isValidMobile(contactDetails["primaryMobileNumber"]))
          return failureToast("Invalid Primary Mobile Number");

        if (!isValidMobile(contactDetails["whatsappNumber"]))
          return failureToast("Invalid Whatsapp Number");

        if (!isValidMobile(contactDetails["studentMobileNumber"]))
          return failureToast("Invalid Student Mobile Number");

        if (!regex.email.test(contactDetails["studentEmailId"]))
          return failureToast("Invalid Student Email ID");

        if (
          !contactDetails["addressLine1"]?.trim() ||
          !contactDetails["addressLine2"]?.trim()
        )
          return failureToast("Please enter Address");

        if (
          !contactDetails["postalCode"]?.trim() ||
          !contactDetails["city"].trim() ||
          !contactDetails["state"].trim() ||
          !contactDetails["country"].trim()
        )
          return failureToast("Please enter valid Pincode");

        const payload1 = {
          primaryMobileNumber: contactDetails["primaryMobileNumber"],
          whatsappNumber: contactDetails["whatsappNumber"],
          studentMobileNumber: contactDetails["studentMobileNumber"],
          studentEmailId: contactDetails["studentEmailId"],
        };

        const payload2 = {
          studentAddressDetailDTOList: [
            {
              addressLine1: contactDetails["addressLine1"],
              addressLine2: contactDetails["addressLine2"],
              postalCode: contactDetails["postalCode"],
              city: contactDetails["city"],
              state: contactDetails["state"],
              country: contactDetails["country"],
              addressType: "HOME",
            },
          ],

          studentDetailTicketInfoDTO: {
            ticketId: ticketDetails.id,
            ticketDate: ticketDetails.date,
            ticketDescription: ticketDetails.description,
          },
        };

        const { api1Response, api2Response } = await updateContactDetails({
          psid: studentDetails.psid,
          payload1,
          payload2,
        });

        if (api1Response?.code === 200 && api2Response?.code === 200) {
          setIsContactDetailsModified(false);
          saveContactDetails(api1Response.data, api2Response.data);
          return successToast(
            `Contact Details successfully updated as part of Ticket Id :${ticketDetails.id}`
          );
        }

        // return failureToast(`Error in updating. Please try again.`);
      },
      isModified: isContactDetailsModified,
      discardChanges: () => {
        setDiscardDialog({
          isOpen: true,
          onConfirm: () => {
            setContactDetails(contactDetailsResponse);
            setIsContactDetailsModified(false);
            closeDiscardDialog();
          },
        });
      },
      onFieldChange: (key, value) => {
        setIsContactDetailsModified(true);
        setContactDetails((prev) => ({ ...prev, [key]: value ?? [] }));
      },
      fields: [
        {
          type: "text",
          label: "Primary Mobile Number*",
          key: "primaryMobileNumber",
          maxlength: 10,
        },
        {
          type: "text",
          label: "Whatsapp Number*",
          key: "whatsappNumber",
          maxlength: 10,
        },
        {
          type: "text",
          label: "Student Mobile Number*",
          key: "studentMobileNumber",
          maxlength: 10,
        },
        {
          type: "text",
          label: "Student Email ID*",
          key: "studentEmailId",
        },
        {
          type: "text",
          label: "GC ID*",
          key: "studentGcId",
          readonly: true,
        },
        {
          type: "text",
          label: "Address Line 1*",
          key: "addressLine1",
        },
        {
          type: "text",
          label: "Address Line 2*",
          key: "addressLine2",
        },
        {
          type: "text",
          label: "Pincode*",
          key: "postalCode",
        },
        {
          type: "text",
          label: "City*",
          key: "city",
          readonly: true,
        },
        {
          type: "text",
          label: "State*",
          key: "state",
          readonly: true,
        },
        {
          type: "text",
          label: "Country*",
          key: "country",
          readonly: true,
        },
      ],
      lastUpdated: contactDetails?.updatedOn
        ? LastUpdatedDetails(contactDetails.updatedOn, contactDetails.updatedBy)
        : "-",
    },
    {
      heading: "Parent/Guardian Information",
      state: parentInfo,
      viewLogs: () => {
        setViewLogsDialog({
          isOpen: true,
          category: "studentParentDetails",
          heading: "Parent/Guardian Information",
          psid: studentDetails.psid,
        });
      },
      saveChanges: async () => {
        if (!parentInfo["studentFatherName"].trim())
          return failureToast("Please enter Father's name");

        if (!parentInfo["fathersMobileNumber"].trim())
          return failureToast("Please enter Father's mobile number");

        if (!isValidMobile(parentInfo["fathersMobileNumber"]))
          return failureToast("Invalid Father's mobile number");

        if (
          parentInfo["mothersMobileNumber"] &&
          !isValidMobile(parentInfo["mothersMobileNumber"])
        )
          return failureToast("Invalid Mother's mobile number");

        if (!regex.email.test(parentInfo["fathersEmailId"]))
          return failureToast("Invalid Father's email ID");

        if (
          parentInfo["mothersEmailId"] &&
          !regex.email.test(parentInfo["mothersEmailId"])
        )
          return failureToast("Invalid Mother's email ID");

        if (!parentInfo["occupationDescription"].trim())
          return failureToast("Please enter Father's Occupation");

        const payload1 = {
          studentFatherName: parentInfo["studentFatherName"],
          studentMotherName: parentInfo["studentMotherName"],
          occupationDescription: parentInfo["occupationDescription"],
          studentMotherOccupation: parentInfo["studentMotherOccupation"],
        };

        const payload2 = {
          studentPhoneDetailDTOList: [
            {
              communicationType: "Phone",
              contactType: "HOME",
              contactValue: parentInfo["fathersMobileNumber"],
              isPreferred: true,
            },
            {
              communicationType: "Phone",
              contactType: "CELL",
              contactValue: parentInfo["mothersMobileNumber"],
            },
          ],
          studentEmailDetailDTOList: [
            {
              communicationType: "Email",
              contactType: "HOME",
              contactValue: parentInfo["fathersEmailId"],
              isPreferred: true,
            },
            {
              communicationType: "Email",
              contactType: "OTHR",
              contactValue: parentInfo["mothersEmailId"],
            },
          ],
          studentDetailTicketInfoDTO: {
            ticketId: ticketDetails.id,
            ticketDate: ticketDetails.date,
            ticketDescription: ticketDetails.description,
          },
        };

        const { api1Response, api2Response } = await updateContactDetails({
          psid: studentDetails.psid,
          payload1,
          payload2,
        });
        if (api1Response?.code === 200 && api2Response?.code === 200) {
          setIsParentInfoModified(false);
          saveGuardianDetails(api1Response.data, api2Response.data);
          return successToast(
            `Parents Details successfully updated as part of Ticket Id :${ticketDetails.id}`
          );
        }

        // return failureToast(`Error in updating. Please try again.`);
      },
      isModified: isParentInfoModified,
      discardChanges: () => {
        setDiscardDialog({
          isOpen: true,
          onConfirm: () => {
            setParentInfo(parentDetailsResponse);
            setIsParentInfoModified(false);
            closeDiscardDialog();
          },
        });
      },
      onFieldChange: (key, value) => {
        setIsParentInfoModified(true);
        setParentInfo((prev) => ({ ...prev, [key]: value ?? [] }));
      },
      fields: [
        {
          type: "text",
          label: "Father's Name*",
          key: "studentFatherName",
        },
        {
          type: "text",
          label: "Mother's Name",
          key: "studentMotherName",
        },
        {
          type: "text",
          label: "Father's Mobile Number*",
          key: "fathersMobileNumber",
          maxlength: 10,
        },
        {
          type: "text",
          label: "Mother's Mobile Number",
          key: "mothersMobileNumber",
          maxlength: 10,
        },
        {
          type: "text",
          label: "Father's email ID*",
          key: "fathersEmailId",
        },
        {
          type: "text",
          label: "Mother's email ID",
          key: "mothersEmailId",
        },
        {
          type: "text",
          label: "Father's Occupation*",
          key: "occupationDescription",
        },
        {
          type: "text",
          label: "Mother's Occupation",
          key: "studentMotherOccupation",
        },
      ],
      lastUpdated: parentInfo.updatedOn
        ? LastUpdatedDetails(parentInfo.updatedOn, parentInfo?.updatedBy)
        : "-",
    },
  ];

  return (
    <div>
      <TicketHeader
        ticketDetails={ticketDetails}
        exitEditMode={exitEditMode}
        changeTicketDetails={changeTicketDetails}
      />

      {sections.map(
        ({
          heading,
          state,
          viewLogs,
          saveChanges,
          isModified,
          discardChanges,
          onFieldChange,
          fields,
          lastUpdated,
        }) => (
          <div className={styles.formSection} key={heading}>
            <div
              className="w-100 d-flex align-items-center justify-content-between p-3 text-left"
              style={{ height: "60px" }}
            >
              <span className="text-bold text-black text-md">{heading}</span>
              <div className="d-flex align-items-center">
                <button
                  onClick={viewLogs}
                  className={`${styles.changeTicketBtn} text-primary text-bold`}
                >
                  View Logs
                </button>
                <div className="d-flex">
                  <button
                    onClick={saveChanges}
                    disabled={!isModified}
                    className={`${styles.saveBtn}`}
                  >
                    Save
                  </button>
                  <button
                    onClick={discardChanges}
                    className={`${styles.discardBtn}`}
                    disabled={!isModified}
                  >
                    Discard
                  </button>
                </div>
              </div>
            </div>

            <div className={`px-3 pb-3 ${styles.form}`}>
              <div className={styles.gridLayout}>
                {fields.map((item) => {
                  const type = () => {
                    switch (item.type) {
                      case "dropdown":
                        return (
                          <AAutoComplete
                            label={item.label}
                            placeholder="Select"
                            items={item.options}
                            currentValue={
                              state[item.key]
                                ? item.options?.find(
                                    (ob) =>
                                      ob.value === state[item.key] ||
                                      ob.label === state[item.key]
                                  )
                                : ""
                            }
                            isShowCount={false}
                            isMulti={false}
                            handleChange={(value) => {
                              onFieldChange(
                                item.key,
                                item.key === "studentCategory"
                                  ? value.label
                                  : value.value
                              );
                            }}
                            height="44px"
                          />
                        );

                      case "date":
                        return (
                          <>
                            <label style={InputLabelStyle}>{item.label}</label>
                            <CustomDatePicker
                              placeHolder="Select"
                              value={state[item.key]}
                              handler={(index, date, keyName) => {
                                onFieldChange(item.key, date);
                              }}
                            />
                          </>
                        );

                      default:
                        return (
                          <AInput
                            type="text"
                            label={item.label}
                            value={state[item.key]}
                            handler={(value) => {
                              onFieldChange(item.key, value);
                            }}
                            onKeyDown={(e) => {
                              const { value } = e.target;
                              if (e.key === "Backspace" && value.length === 1)
                                onFieldChange(item.key, "");
                            }}
                            placeholder="Enter"
                            height="44px"
                            style={{ height: "44px" }}
                            readonly={item.readonly ?? false}
                            disabled={item.readonly ?? false}
                            maxlength={item?.maxlength}
                          />
                        );
                    }
                  };

                  return <div key={item.key}>{type()}</div>;
                })}
              </div>
              <div className="mt-4 text-xs text-gray text-right">
                Last Updated On/By: {lastUpdated}
              </div>
            </div>
          </div>
        )
      )}

      {viewLogsDialog.isOpen && (
        <ViewEditLogs
          open={viewLogsDialog.isOpen}
          heading={viewLogsDialog.heading}
          closeLogs={() => {
            setViewLogsDialog({
              isOpen: false,
              category: "",
              heading: "",
              psid: "",
            });
          }}
          category={viewLogsDialog.category}
          psid={viewLogsDialog.psid}
        />
      )}

      {discardDialog.isOpen && (
        <DiscardDialog
          isOpen={discardDialog.isOpen}
          onReject={closeDiscardDialog}
          onConfirm={discardDialog.onConfirm}
        />
      )}
    </div>
  );
};

export default EditStudentDetails;
